import React, { useState, useEffect, useCallback } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { API_BASE } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTrackSection } from "@/hooks/useTrackSection";
import { 
  Brain, Target, BookOpen, FileText, ArrowLeft, Loader2, 
  ChevronDown, ChevronUp, Lock, Sparkles, CheckCircle2,
  BookmarkPlus, PenTool, HelpCircle, Calendar,
  AlertCircle, Info, Shield, Scale, Zap,
  RefreshCw, TrendingUp, Clock, Newspaper, Flame,
  ChevronRight, Eye, Filter, Download
} from "lucide-react";

/**
 * CLARIAS Daily Intelligence - SuperKalam Inspired Vibrant Design
 * 
 * Exam-oriented analysis of current events for UPSC aspirants.
 * Should I care? How deeply? Will UPSC ask?
 */

const DailyIntelligencePage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  useTrackSection(); // Track section visit
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [intelligenceItems, setIntelligenceItems] = useState([]);
  const [pibReleases, setPibReleases] = useState([]);
  const [showPib, setShowPib] = useState(false);
  const [showMcqs, setShowMcqs] = useState(false);
  const [mcqs, setMcqs] = useState([]);
  const [mcqProgress, setMcqProgress] = useState(null);
  const [mcqLoading, setMcqLoading] = useState(false);
  const [currentMcqIndex, setCurrentMcqIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [mcqResult, setMcqResult] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(() => {
    // Get today's date in YYYY-MM-DD format (local timezone)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  const [pibStats, setPibStats] = useState({ total: 0, high: 0 });
  const [error, setError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);

  const isPro = user?.role === "PRO";
  const firstName = user?.name?.split(" ")[0] || "Aspirant";

  // Fetch intelligence from API
  const fetchIntelligence = useCallback(async () => {
    // Wait for auth to be ready
    const authData = localStorage.getItem("ranker-auth");
    if (!authData) {
      setError("Please login to access UPSC Intelligence");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await managedAxios.get(
        `${API_BASE}/intelligence/daily?date=${selectedDate}`
      );
      
      const data = response.data;
      
      setStats({
        total: data.total_items || 0,
        high: data.high_priority || 0,
        medium: data.medium_priority || 0,
        low: data.low_priority || 0
      });
      
      let items = data.items || [];
      
      // Apply filter
      if (filter !== "all") {
        if (filter === "international") {
          items = items.filter(item => item.category === "international");
        } else if (filter.startsWith("gs")) {
          items = items.filter(item => 
            item.gs_papers?.some(p => p.toLowerCase() === filter.toUpperCase())
          );
        } else if (filter === "high") {
          items = items.filter(item => item.relevance_level === "HIGH");
        } else if (filter === "medium") {
          items = items.filter(item => item.relevance_level === "MEDIUM");
        }
      }
      
      setIntelligenceItems(items);
    } catch (err) {
      // Ignore cancel errors (happens on route change)
      if (isCancelError(err)) {
        console.log("Intelligence fetch cancelled (route change)");
        return;
      }
      console.error("Error fetching intelligence:", err);
      if (err.response?.status === 401) {
        setError("Please login to access UPSC Intelligence");
      } else if (err.response?.status === 404) {
        // No data for this date - not an error, just empty
        setStats({ total: 0, high: 0, medium: 0, low: 0 });
        setIntelligenceItems([]);
        setError(null);
      } else {
        setError("Unable to load intelligence data. Please try again later.");
      }
      setIntelligenceItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, filter]);

  // Fetch available dates
  const fetchAvailableDates = useCallback(async () => {
    try {
      const response = await managedAxios.get(
        `${API_BASE}/intelligence/available-dates`
      );
      setAvailableDates(response.data.available_dates || []);
    } catch (err) {
      console.error("Error fetching available dates:", err);
    }
  }, []);

  // Refresh intelligence (PRO only)
  const handleRefresh = async () => {
    if (!isPro) return;
    
    setRefreshing(true);
    try {
      await managedAxios.post(
        `${API_BASE}/intelligence/refresh`,
        {}
      );
      setTimeout(() => {
        fetchIntelligence();
        setRefreshing(false);
      }, 5000);
    } catch (err) {
      console.error("Error refreshing intelligence:", err);
      setRefreshing(false);
    }
  };

  // Download single item as PDF
  const downloadItem = async (itemId, itemTitle) => {
    try {
      const response = await fetch(`${API_BASE}/intelligence/download/item/${itemId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CLARIAS_${selectedDate}_${itemTitle.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  // Download all items for selected date as PDF
  const downloadAllItems = async () => {
    try {
      const response = await fetch(`${API_BASE}/intelligence/download/daily/${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CLARIAS_Daily_Intelligence_${selectedDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  // Download weekly compilation
  const downloadWeekly = async () => {
    try {
      const response = await fetch(`${API_BASE}/intelligence/download/weekly?end_date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CLARIAS_Weekly_Intelligence_${selectedDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  // Fetch PIB (Press Information Bureau) releases
  const fetchPibReleases = useCallback(async () => {
    try {
      const response = await managedAxios.get(
        `${API_BASE}/intelligence/pib/daily?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        setPibReleases(response.data.releases || []);
        setPibStats({
          total: response.data.total_releases || 0,
          high: response.data.high_priority || 0
        });
      }
    } catch (err) {
      console.error('Failed to fetch PIB releases:', err);
    }
  }, [selectedDate, token]);

  // Fetch Daily CA MCQs
  const fetchMcqs = useCallback(async () => {
    setMcqLoading(true);
    try {
      const response = await managedAxios.get(
        `${API_BASE}/intelligence/mcqs/date/${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        setMcqs(response.data.mcqs || []);
        setMcqProgress(response.data.user_progress || null);
        setCurrentMcqIndex(0);
        setSelectedAnswer(null);
        setMcqResult(null);
      }
    } catch (err) {
      console.error('Failed to fetch MCQs:', err);
      setMcqs([]);
    } finally {
      setMcqLoading(false);
    }
  }, [selectedDate, token]);

  // Submit MCQ answer
  const submitMcqAnswer = async () => {
    if (!selectedAnswer || !mcqs[currentMcqIndex]) return;
    
    try {
      const response = await managedAxios.post(
        `${API_BASE}/intelligence/mcqs/submit/${mcqs[currentMcqIndex].id}?answer=${selectedAnswer}&time_taken=60`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMcqResult(response.data);
      
      // Update progress
      setMcqProgress(prev => ({
        ...prev,
        attempted: (prev?.attempted || 0) + 1,
        correct: (prev?.correct || 0) + (response.data.is_correct ? 1 : 0)
      }));
    } catch (err) {
      console.error('Failed to submit MCQ:', err);
    }
  };

  // Go to next MCQ
  const nextMcq = () => {
    if (currentMcqIndex < mcqs.length - 1) {
      setCurrentMcqIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setMcqResult(null);
    }
  };

  // Go to previous MCQ
  const prevMcq = () => {
    if (currentMcqIndex > 0) {
      setCurrentMcqIndex(prev => prev - 1);
      setSelectedAnswer(null);
      setMcqResult(null);
    }
  };

  useEffect(() => {
    fetchIntelligence();
    fetchAvailableDates();
    fetchPibReleases();
  }, [fetchIntelligence, fetchAvailableDates, fetchPibReleases]);

  // Fetch MCQs when showMcqs is enabled
  useEffect(() => {
    if (showMcqs && selectedDate) {
      fetchMcqs();
    }
  }, [showMcqs, selectedDate, fetchMcqs]);

  const toggleExpand = async (id, item) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Track the read when user expands to read the article
      if (token && item) {
        try {
          await managedAxios.post(
            `${API_BASE}/intelligence/track-read`,
            { 
              title: item.event_title || "Daily Intelligence",
              item_id: item.id,
              category: item.gs_papers?.[0] || "general"
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Mark CA target as complete for daily targets
          const todayRes = await fetch(`${API_BASE}/daily-targets/today`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (todayRes.ok) {
            const todayData = await todayRes.json();
            await fetch(`${API_BASE}/daily-targets/mark-complete?target_id=${todayData.id}&completion_type=ca`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        } catch (e) {
          // Silently fail
        }
      }
    }
    setExpandedItems(newExpanded);
  };

  // Relevance Badge
  const getRelevanceBadge = (level) => {
    switch (level) {
      case "HIGH":
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">🟢 High</Badge>;
      case "MEDIUM":
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">🟡 Medium</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 border-slate-200">🔴 Low</Badge>;
    }
  };

  const getGSBadge = (gs) => {
    const colors = {
      "GS1": "bg-orange-100 text-orange-700 border-orange-200",
      "GS2": "bg-blue-100 text-blue-700 border-blue-200",
      "GS3": "bg-emerald-100 text-emerald-700 border-emerald-200",
      "GS4": "bg-purple-100 text-purple-700 border-purple-200"
    };
    return <Badge className={colors[gs] || "bg-slate-100 text-slate-700"}>{gs}</Badge>;
  };

  const getRevisionTagBadge = (tag) => {
    const tagConfig = {
      "static-heavy": { label: "Static Heavy", color: "bg-blue-50 text-blue-600 border-blue-200" },
      "ca-heavy": { label: "CA Heavy", color: "bg-orange-50 text-orange-600 border-orange-200" },
      "hybrid": { label: "Hybrid", color: "bg-purple-50 text-purple-600 border-purple-200" },
      "one-time": { label: "One-Time", color: "bg-slate-50 text-slate-600 border-slate-200" },
      "long-term": { label: "Long-Term", color: "bg-teal-50 text-teal-600 border-teal-200" }
    };
    const config = tagConfig[tag] || tagConfig["ca-heavy"];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'undefined' || dateStr === 'null') {
      return 'Today';
    }
    try {
      // Parse date string directly to avoid timezone issues
      const [year, month, day] = dateStr.split('-').map(Number);
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return 'Today';
      }
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-IN', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (e) {
      return 'Today';
    }
  };

  // Loading state
  if (loading && intelligenceItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-cyan-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <Newspaper className="w-6 h-6 text-cyan-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-cyan-300 font-medium">Loading Intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/30">
      
      {/* ═══════════════════════════════════════════════════════════
          VIBRANT HEADER
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Top Row - Back & Title */}
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Newspaper className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Daily Intelligence</h1>
                <p className="text-sm text-cyan-100">UPSC-Focused Current Affairs</p>
              </div>
            </div>
            {isPro && (
              <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 font-semibold px-3 py-1">
                <Flame className="w-3 h-3 mr-1" />
                PRO
              </Badge>
            )}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-[10px] text-cyan-200">Total</p>
            </div>
            <div className="bg-emerald-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{stats.high}</p>
              <p className="text-[10px] text-emerald-200">🟢 High</p>
            </div>
            <div className="bg-amber-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{stats.medium}</p>
              <p className="text-[10px] text-amber-200">🟡 Medium</p>
            </div>
            <div className="bg-slate-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{stats.low}</p>
              <p className="text-[10px] text-slate-200">🔴 Low</p>
            </div>
          </div>
          
          {/* Download Buttons */}
          <div className="flex gap-2 mt-3">
            <Button
              onClick={downloadAllItems}
              disabled={stats.total === 0}
              className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0 text-xs py-2"
              data-testid="download-daily-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Download Today's PDF
            </Button>
            <Button
              onClick={downloadWeekly}
              className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0 text-xs py-2"
              data-testid="download-weekly-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Weekly PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        
        {/* ═══════════════════════════════════════════════════════════
            FILTERS CARD
            ═══════════════════════════════════════════════════════════ */}
        <Card className="border-0 shadow-lg bg-white overflow-hidden">
          <div className="bg-gradient-to-r from-slate-100 to-blue-50 px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-600" />
              <span className="font-semibold text-sm text-slate-700">Filters</span>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Date Picker */}
              <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 py-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-transparent border-none text-sm font-medium text-slate-700 focus:outline-none"
                />
              </div>

              {/* Filter Pills */}
              <div className="flex flex-wrap gap-2">
                {[
                  { id: "all", label: "All", color: "bg-slate-100 hover:bg-slate-200 text-slate-700" },
                  { id: "high", label: "High", color: "bg-emerald-100 hover:bg-emerald-200 text-emerald-700" },
                  { id: "medium", label: "Medium", color: "bg-amber-100 hover:bg-amber-200 text-amber-700" },
                  { id: "international", label: "International", color: "bg-sky-100 hover:bg-sky-200 text-sky-700" },
                  { id: "gs1", label: "GS1", color: "bg-orange-100 hover:bg-orange-200 text-orange-700" },
                  { id: "gs2", label: "GS2", color: "bg-blue-100 hover:bg-blue-200 text-blue-700" },
                  { id: "gs3", label: "GS3", color: "bg-emerald-100 hover:bg-emerald-200 text-emerald-700" },
                  { id: "gs4", label: "GS4", color: "bg-purple-100 hover:bg-purple-200 text-purple-700" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      filter === f.id 
                        ? `${f.color} ring-2 ring-offset-1 ring-blue-400` 
                        : `${f.color}`
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
                
                {/* PIB Toggle */}
                <button
                  onClick={() => { setShowPib(!showPib); setShowMcqs(false); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                    showPib 
                      ? 'bg-indigo-500 text-white ring-2 ring-offset-1 ring-indigo-400' 
                      : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
                  }`}
                  data-testid="pib-toggle-btn"
                >
                  <Shield className="w-3 h-3" /> PIB ({pibStats.total})
                </button>

                {/* MCQ Toggle */}
                <button
                  onClick={() => { setShowMcqs(!showMcqs); setShowPib(false); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                    showMcqs 
                      ? 'bg-emerald-500 text-white ring-2 ring-offset-1 ring-emerald-400' 
                      : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                  }`}
                  data-testid="mcq-toggle-btn"
                >
                  <Target className="w-3 h-3" /> Practice MCQs
                </button>
              </div>

              {/* Refresh Button (PRO only) */}
              {isPro && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="ml-auto"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            INFO BANNER
            ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-2xl p-4 border border-blue-200/50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-blue-900 text-sm">CLARIAS Intelligence Engine</p>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                Exam-oriented analysis of latest news. News dates reflect original publication; analysis is done daily.
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            ERROR STATE
            ═══════════════════════════════════════════════════════════ */}
        {error && (
          <Card className="border-red-200 bg-gradient-to-r from-red-50 to-orange-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            PIB RELEASES SECTION
            ═══════════════════════════════════════════════════════════ */}
        {showPib && pibReleases.length > 0 && (
          <Card className="border-0 shadow-lg bg-gradient-to-r from-indigo-50 to-purple-50 mb-6">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <span className="text-lg">🏛️</span>
                  <span className="font-bold">PIB Official Releases</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    {pibStats.total} releases • {pibStats.high} high priority
                  </span>
                </div>
                <button 
                  onClick={() => setShowPib(false)}
                  className="text-white/80 hover:text-white text-sm"
                >
                  Hide
                </button>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {pibReleases.map((release, idx) => (
                  <a
                    key={release.id || idx}
                    href={release.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-3 rounded-lg transition-all hover:shadow-md ${
                      release.relevance === 'HIGH'
                        ? 'bg-emerald-50 border-l-4 border-emerald-500'
                        : 'bg-white border-l-4 border-indigo-300'
                    }`}
                    data-testid={`pib-release-${release.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-800 text-sm leading-tight">
                          {release.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5">
                          {release.gs_papers?.map(gs => (
                            <span key={gs} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                              {gs}
                            </span>
                          ))}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            release.relevance === 'HIGH' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {release.relevance}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                    </div>
                  </a>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3 text-center">
                Source: Press Information Bureau, Government of India
              </p>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            MCQ PRACTICE SECTION
            ═══════════════════════════════════════════════════════════ */}
        {showMcqs && (
          <Card className="border-0 shadow-lg bg-gradient-to-r from-emerald-50 to-teal-50 mb-6">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Target className="w-5 h-5" />
                  <span className="font-bold">Practice MCQs from Today's News</span>
                  {mcqProgress && (
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                      {mcqProgress.attempted}/{mcqs.length} attempted • {mcqProgress.correct} correct
                    </span>
                  )}
                </div>
                <button 
                  onClick={() => setShowMcqs(false)}
                  className="text-white/80 hover:text-white text-sm"
                >
                  Hide
                </button>
              </div>
            </div>
            <CardContent className="p-4">
              {mcqLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                  <span className="ml-2 text-slate-600">Loading MCQs...</span>
                </div>
              ) : mcqs.length === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No MCQs available for this date yet.</p>
                  <p className="text-xs text-slate-400 mt-1">MCQs are generated from daily news analysis.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Progress Bar */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${((currentMcqIndex + 1) / mcqs.length) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 font-medium">
                      {currentMcqIndex + 1}/{mcqs.length}
                    </span>
                  </div>

                  {/* Current MCQ */}
                  {mcqs[currentMcqIndex] && (
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                      {/* Question Header */}
                      <div className="flex items-start gap-3 mb-4">
                        <span className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center font-bold text-sm">
                          Q{currentMcqIndex + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className={`text-xs ${
                              mcqs[currentMcqIndex].difficulty === 'easy' ? 'border-green-500 text-green-600' :
                              mcqs[currentMcqIndex].difficulty === 'hard' ? 'border-red-500 text-red-600' :
                              'border-amber-500 text-amber-600'
                            }`}>
                              {mcqs[currentMcqIndex].difficulty}
                            </Badge>
                            {mcqs[currentMcqIndex].gs_papers?.map(gs => (
                              <Badge key={gs} variant="secondary" className="text-xs">
                                {gs}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-slate-800 font-medium leading-relaxed whitespace-pre-line">
                            {mcqs[currentMcqIndex].question}
                          </p>
                          <p className="text-xs text-slate-400 mt-2">
                            Source: {mcqs[currentMcqIndex].source_news}
                          </p>
                        </div>
                      </div>

                      {/* Options */}
                      <div className="space-y-2 ml-11">
                        {mcqs[currentMcqIndex].options?.map((option) => {
                          const isSelected = selectedAnswer === option.label;
                          const isCorrect = mcqResult?.correct_answer === option.label;
                          const isWrong = mcqResult && isSelected && !mcqResult.is_correct;
                          
                          return (
                            <button
                              key={option.label}
                              onClick={() => !mcqResult && setSelectedAnswer(option.label)}
                              disabled={!!mcqResult}
                              className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                mcqResult
                                  ? isCorrect
                                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                                    : isWrong
                                      ? 'border-red-500 bg-red-50 text-red-800'
                                      : 'border-slate-200 bg-slate-50 text-slate-600'
                                  : isSelected
                                    ? 'border-emerald-500 bg-emerald-50'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                              data-testid={`mcq-option-${option.label}`}
                            >
                              <span className="font-medium mr-2">({option.label})</span>
                              {option.text}
                              {mcqResult && isCorrect && (
                                <CheckCircle2 className="inline-block ml-2 w-4 h-4 text-emerald-600" />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Explanation (shown after answering) */}
                      {mcqResult && (
                        <div className={`mt-4 ml-11 p-3 rounded-lg ${
                          mcqResult.is_correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            {mcqResult.is_correct ? (
                              <>
                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                <span className="font-semibold text-emerald-700">Correct!</span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                                <span className="font-semibold text-amber-700">Incorrect</span>
                              </>
                            )}
                          </div>
                          <p className="text-sm text-slate-700">{mcqResult.explanation}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center justify-between mt-4 ml-11">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={prevMcq}
                          disabled={currentMcqIndex === 0}
                        >
                          Previous
                        </Button>
                        
                        {!mcqResult ? (
                          <Button
                            onClick={submitMcqAnswer}
                            disabled={!selectedAnswer}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            Submit Answer
                          </Button>
                        ) : (
                          <Button
                            onClick={nextMcq}
                            disabled={currentMcqIndex === mcqs.length - 1}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            Next Question
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* MCQ Navigation Dots */}
                  <div className="flex items-center justify-center gap-1 mt-4">
                    {mcqs.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setCurrentMcqIndex(idx);
                          setSelectedAnswer(null);
                          setMcqResult(null);
                        }}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentMcqIndex
                            ? 'w-6 bg-emerald-500'
                            : 'bg-slate-300 hover:bg-slate-400'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            INTELLIGENCE ITEMS
            ═══════════════════════════════════════════════════════════ */}
        {loading && intelligenceItems.length > 0 && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="ml-2 text-slate-600">Updating...</span>
          </div>
        )}

        <div className="space-y-4">
          {intelligenceItems.map((item, idx) => (
            <Card 
              key={item.id || idx}
              className={`border-0 shadow-md overflow-hidden transition-all hover:shadow-lg ${
                item.relevance_level === "HIGH" 
                  ? "bg-gradient-to-r from-white to-emerald-50/30 border-l-4 border-l-emerald-500" 
                  : item.relevance_level === "MEDIUM"
                  ? "bg-gradient-to-r from-white to-amber-50/30 border-l-4 border-l-amber-500"
                  : "bg-white border-l-4 border-l-slate-300"
              }`}
            >
              {/* Card Header */}
              <div 
                className="p-4 cursor-pointer"
                onClick={() => toggleExpand(item.id, item)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2 flex-1">
                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5">
                      {item.gs_papers?.map(gs => (
                        <span key={gs}>{getGSBadge(gs)}</span>
                      ))}
                      <Badge variant="outline" className="text-xs">{item.exam_stage}</Badge>
                      {getRelevanceBadge(item.relevance_level)}
                      {item.revision_tag && getRevisionTagBadge(item.revision_tag)}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-base font-semibold text-slate-900 leading-tight">
                      {item.event_title}
                    </h3>
                    
                    {/* Date */}
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{(item.date || selectedDate).split('-').reverse().join('-')}</span>
                    </div>
                    
                    {/* GS Paper Badge */}
                    <Badge variant="outline" className="text-xs">
                      {item.gs_paper}
                    </Badge>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    {/* Download Button */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); downloadItem(item.id, item.event_title || 'item'); }}
                      className="p-2 rounded-lg hover:bg-blue-100 transition-colors"
                      title="Download PDF"
                      data-testid={`download-item-${item.id}`}
                    >
                      <Download className="w-4 h-4 text-blue-500" />
                    </button>
                    
                    {/* Expand Button */}
                    <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                      {expandedItems.has(item.id) ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Context Preview */}
                <p className="mt-3 text-sm text-slate-600 line-clamp-2">{item.event_context}</p>
              </div>

              {/* Expanded Content */}
              {expandedItems.has(item.id) && (
                <CardContent className="border-t border-slate-100 bg-slate-50/50 space-y-5 p-4">
                  {/* Event Context */}
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4 text-blue-500" />
                      Event Context
                    </h4>
                    <p className="text-sm text-slate-700">{item.event_context}</p>
                  </div>

                  {/* Syllabus Connection */}
                  {item.background_linkage && (
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-purple-500" />
                        Syllabus Connection
                      </h4>
                      <p className="text-sm text-slate-700">{item.background_linkage}</p>
                      
                      {/* Related Items */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.related_articles?.map((art, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-blue-50">{art}</Badge>
                        ))}
                        {item.related_acts?.map((act, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-purple-50">{act}</Badge>
                        ))}
                        {item.related_institutions?.map((inst, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-slate-50">{inst}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Exam-Ready Facts */}
                  {item.key_facts?.length > 0 && (
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                      <h4 className="font-semibold text-emerald-900 flex items-center gap-2 mb-3">
                        <Target className="w-4 h-4 text-emerald-600" />
                        Exam-Ready Facts
                      </h4>
                      <ul className="space-y-2">
                        {item.key_facts.map((fact, idx) => (
                          <li key={idx} className="text-sm text-emerald-800 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {fact}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Why UPSC Cares */}
                  <div>
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Why UPSC Cares
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Prelims Relevance */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                        <h5 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Prelims Relevance
                        </h5>
                        <ul className="space-y-1.5">
                          {item.prelims_relevance?.map((point, idx) => (
                            <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                              <CheckCircle2 className="w-3 h-3 mt-1 text-blue-500 flex-shrink-0" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Mains Relevance */}
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                        <h5 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                          <PenTool className="w-4 h-4" />
                          Mains Relevance
                        </h5>
                        <ul className="space-y-1.5">
                          {item.mains_relevance?.map((point, idx) => (
                            <li key={idx} className="text-sm text-purple-800 flex items-start gap-2">
                              <CheckCircle2 className="w-3 h-3 mt-1 text-purple-500 flex-shrink-0" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Mains Dimensions */}
                  {item.dimensions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                        <Scale className="w-4 h-4 text-teal-500" />
                        Mains Dimensions
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {item.dimensions.map((dim, idx) => (
                          <Badge key={idx} className="bg-teal-100 text-teal-700 border-teal-200">
                            {dim}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PYQ Intelligence - PRO Only */}
                  {isPro && item.pyq_themes?.length > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
                      <h4 className="font-semibold text-orange-900 flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-orange-600" />
                        PYQ Intelligence
                      </h4>
                      <ul className="space-y-1">
                        {item.pyq_themes.map((theme, idx) => (
                          <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                            <span className="text-orange-500">•</span>
                            {theme}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Practice Questions - PRO Only */}
                  {isPro ? (
                    <div>
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
                        <HelpCircle className="w-4 h-4 text-indigo-500" />
                        Practice Questions
                      </h4>
                      
                      {/* Prelims MCQ */}
                      {item.prelims_mcqs?.length > 0 && item.prelims_mcqs[0] && (
                        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-3">
                          <Badge className="mb-3 bg-blue-100 text-blue-700">Prelims MCQ</Badge>
                          <p className="font-medium text-slate-900 mb-3">{item.prelims_mcqs[0].question}</p>
                          <div className="space-y-2">
                            {item.prelims_mcqs[0].options?.map((opt, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border text-sm ${
                                  idx === item.prelims_mcqs[0].correct_index 
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
                                    : 'bg-slate-50 border-slate-200 text-slate-700'
                                }`}
                              >
                                <span className="font-medium">{String.fromCharCode(65 + idx)}.</span> {opt}
                                {idx === item.prelims_mcqs[0].correct_index && (
                                  <span className="ml-2 text-emerald-600 font-medium">✓ Correct</span>
                                )}
                              </div>
                            ))}
                          </div>
                          {item.prelims_mcqs[0].explanation && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-800">
                                <strong>Explanation:</strong> {item.prelims_mcqs[0].explanation}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Mains Question */}
                      {item.mains_questions?.length > 0 && item.mains_questions[0] && (
                        <div className="bg-white rounded-xl p-4 border border-slate-200">
                          <Badge className="mb-3 bg-purple-100 text-purple-700">
                            Mains ({item.mains_questions[0].marks} marks)
                          </Badge>
                          <p className="font-medium text-slate-900 mb-3">{item.mains_questions[0].question}</p>
                          {item.mains_questions[0].approach_points?.length > 0 && (
                            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                              <p className="text-sm font-medium text-purple-900 mb-2">Suggested Approach:</p>
                              <ol className="list-decimal list-inside space-y-1">
                                {item.mains_questions[0].approach_points.map((step, idx) => (
                                  <li key={idx} className="text-sm text-purple-800">{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div 
                      className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate('/upgrade')}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Lock className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-purple-900">PRO Feature</p>
                          <p className="text-sm text-purple-700">Practice Questions & PYQ Mapping</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-purple-500" />
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {item.keywords?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-slate-500" />
                        Revision Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {item.keywords.map((kw, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-white">{kw}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons - PRO Only */}
                  {isPro && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                      <Button size="sm" variant="outline" className="text-xs">
                        <BookmarkPlus className="w-3 h-3 mr-1" />
                        Save to Revision
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs">
                        <PenTool className="w-3 h-3 mr-1" />
                        Convert to Notes
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}

          {/* Empty State */}
          {intelligenceItems.length === 0 && !loading && (
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Newspaper className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium mb-2">No intelligence for this date</p>
                <p className="text-xs text-slate-400 max-w-sm mx-auto">
                  {isPro 
                    ? "Click 'Refresh' to generate new intelligence for today" 
                    : "Intelligence is generated daily at 6 AM IST"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            LEGAL DISCLAIMER
            ═══════════════════════════════════════════════════════════ */}
        <div className="text-center pt-6 border-t border-slate-200">
          <p className="text-xs text-slate-400 max-w-lg mx-auto">
            Content on CLARIAS is independently created for educational purposes 
            using publicly available information. It does not reproduce, 
            republish, or aggregate news articles.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyIntelligencePage;
